import { Tooltip } from '@material-ui/core';
import React from 'react';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  Col,
  Cols,
  Div,
  ISimpleOptionSelect,
  spacingV2,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { isPublica } from '~/src/dash/resellers/utils/importResellerConfig';
import { publicaColors } from '~/src/dash/utils/colors';
import { isLanguageOversized } from '~/src/locales/common';

interface IPillSwitcherV2Props {
  isDisabled?: boolean;
  options: ISimpleOptionSelect[];
  onChange: (value: string) => void;
  size?: string;
  tooltipText?: string;
  backgroundColor?: string;
}

interface IPillDimension {
  width: number;
  height: number;
  borderRadius: number;
  unit: string;
}

const pillSizeToDimenions: { [key: string]: IPillDimension } = {
  extraSmall: { width: 3, height: 2, borderRadius: 0.5, unit: 'rem' },
  small: { width: 6, height: 2, borderRadius: 0.5, unit: 'rem' },
  smallLong: { width: 8, height: 2, borderRadius: 0.5, unit: 'rem' },
  smallExtraLong: { width: 10, height: 2, borderRadius: 0.5, unit: 'rem' },
  base: { width: 7.5, height: 2.5, borderRadius: 0.5, unit: 'rem' },
  baseLong: { width: 9.5, height: 2.5, borderRadius: 0.5, unit: 'rem' },
  baseExtraLong: { width: 11.5, height: 2.5, borderRadius: 0.5, unit: 'rem' },
};

export const PillSwitcherV2 = ({
  isDisabled = false,
  options,
  onChange,
  size = 'small',
  tooltipText = '',
  backgroundColor = publicaColors.veryLightGray,
}: IPillSwitcherV2Props) => {
  const { i18n } = usePublicaTranslation();

  // get the pill dimensions based on the size prop
  const pillSize = pillSizeToDimenions[size] || pillSizeToDimenions.small;
  const pillWidth = `${pillSize.width}${pillSize.unit}`;
  const pillHeight = `${pillSize.height}${pillSize.unit}`;
  const pillBorderRadius = `${pillSize.borderRadius}${pillSize.unit}`;

  // default colors for the selected tab
  let activeBackground = isPublica() ? publicaColors.honeydew : publicaColors.veryLightRed;
  let activeTextColor = isPublica() ? publicaColors.teal : publicaColors.red;
  if (isDisabled) {
    activeBackground = publicaColors.lightGray;
    activeTextColor = publicaColors.darkGray;
  }

  // handler when user clicks on a tab
  const handleChange = (newOption: ISimpleOptionSelect) => {
    const selectedOption = options.find((option) => option.value === newOption.value);
    if (selectedOption) {
      onChange(selectedOption.value);
    }
  };

  return (
    <Tooltip title={tooltipText}>
      <Div style={{ position: 'relative', width: 'fit-content' }}>
        <Cols
          noPadding
          style={{
            alignItems: 'center',
            background: backgroundColor,
            borderRadius: pillBorderRadius,
            pointerEvents: isDisabled ? 'none' : 'all',
          }}
        >
          <Div
            style={{
              boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.05)',
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              transition: 'margin 0.3s ease-out',
              background: activeBackground,
              borderRadius: pillBorderRadius,
              width: pillWidth,
              height: pillHeight,
              marginLeft: `${options.findIndex((option) => option.isSelected) * pillSize.width}${
                pillSize.unit
              }`,
            }}
          />
          {options.map((option: ISimpleOptionSelect) => (
            <Col
              displayFlex
              onClick={() => handleChange(option)}
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                width: pillWidth,
                height: pillHeight,
              }}
            >
              {option.customComponent || (
                <Cols style={{ alignItems: 'center' }}>
                  {option.icon && (
                    <Col
                      displayFlex
                      style={{ alignItems: 'center' }}
                      marginRight={spacingV2.extraSmall}
                    >
                      <IconV2
                        name={option.icon}
                        small={size?.includes('small')}
                        color={option.isSelected ? activeTextColor : publicaColors.gray}
                      />
                    </Col>
                  )}
                  <Col>
                    <Text
                      sizeV2
                      small={!isLanguageOversized(i18n.language)}
                      extraSmall={isLanguageOversized(i18n.language)}
                      color={option.isSelected ? activeTextColor : publicaColors.gray}
                      bold={option.isSelected}
                      muted={!option.isSelected}
                      tKey={option.tKey}
                    >
                      {option.label}
                    </Text>
                  </Col>
                </Cols>
              )}
            </Col>
          ))}
        </Cols>
      </Div>
    </Tooltip>
  );
};
